import {ConnectionType, DataType, Location, MeasurementUnit, TimeRange} from "@flowmaps-com/flowmaps-typescriptmodels";
import lodash from "lodash";
import {Injectable} from "@angular/core";
import {DecimalPipe} from "@angular/common";
import {DashboardContext} from "../dashboard/dashboard.context";
import {TooltipModel} from "chart.js/dist/types";
import moment from "moment";
import {localTimeFormat} from "../../common/utils";
import {DashboardTime} from "../../utils/chart-data-provider";
import {ChartCompare} from "../dashboard/dashboard.types";

@Injectable()
export class ChartUtilsService {

    constructor(private decimalPipe: DecimalPipe) {

    }

    percentageFormatter = (value: number): string => `${this.transformDecimal(lodash.round(value * 100, 1))}%`;

    getCustomTooltipFormatter(measurementType: DataType, showBySquareMeter: boolean = false): (ctx: TooltipModel<any>, value: number) => string {
        switch(measurementType) {
            case DataType.sunHours: return (ctx: TooltipModel<any>, value: number) => this.percentageFormatter(value);
            default: return (ctx: TooltipModel<any>, value: number) => this.defaultTooltipFormatter(value, measurementType, showBySquareMeter);
        }
    }

    defaultTooltipFormatter = (value: number, measurementType: DataType, showBySquareMeter: boolean = false): string => {
        const unit = DashboardContext.getMeasurementUnit(measurementType);
        return `${this.transformDecimal(value, ChartUtilsService.fractionDigitsForUnit(unit, showBySquareMeter))} `
            + `${unit}${showBySquareMeter ? '/m²' : ''}`;
    }

    getNumberTooltipFormatter = (): (ctx: TooltipModel<any>, value: number) => string =>
        (ctx: TooltipModel<any>, value) => `${this.transformDecimal(value)}`

    transformDecimal = (value: number, digitsInfo?: string) => this.decimalPipe.transform(value, digitsInfo);

    static fractionDigitsForUnit(unit?: string, showBySquareMeter: boolean = false): string | undefined {
        return unit === MeasurementUnit.kWh && !showBySquareMeter ? '1.0-0' : undefined;
    }

    static createDiagonalPattern(color = 'black', direction: 'ltr' | 'rtl' = 'ltr'): CanvasPattern {
        let shape = document.createElement('canvas');
        shape.width = shape.height = 10;
        let c = shape.getContext('2d');
        c.strokeStyle = color;
        c.beginPath();
        if (direction === "ltr") {
            c.moveTo(2, 0);
            c.lineTo(10, 8);
            c.stroke();
            c.beginPath();
            c.moveTo(0, 8);
            c.lineTo(2, 10);
        } else {
            c.moveTo(8, 0);
            c.lineTo(0, 8);
            c.stroke();
            c.beginPath();
            c.moveTo(10, 8);
            c.lineTo(8, 10);
        }
        c.stroke();
        return c.createPattern(shape, 'repeat');
    }

    static getForYear(timeRange: DashboardTime, year: number): DashboardTime {
        const time = {
            start: moment(timeRange.start),
            end: moment(timeRange.end)
        };
        const yearsAgo = time.start.year() - year;
        return {
            start: time.start.subtract(yearsAgo, "year").startOf(timeRange.resolution).format(localTimeFormat),
            end: time.end.subtract(yearsAgo, "year").startOf(timeRange.resolution).format(localTimeFormat),
            resolution: timeRange.resolution
        }
    }

    static getComparedYear(compare: ChartCompare, timeRange: TimeRange) {
        return compare.relative
            ? moment(timeRange.start).subtract(compare.comparedYear, 'year').year()
            : compare.comparedYear;
    }

    static getEnergyProfileChartTitleKey(location: Location): string {
        const connections = location?.connections || [];
        const hasGasConnection = connections.some(connection =>
            connection.info?.connectionType === ConnectionType.Gas && this.isConnectionAvailable(connection));
        const hasHeatConnection = connections.some(connection =>
            connection.info?.connectionType === ConnectionType.Heat && this.isConnectionAvailable(connection));

        if (hasGasConnection && hasHeatConnection) {
            return 'Gas/heat vs outdoor temperature';
        }
        if (hasHeatConnection) {
            return 'Heat vs outdoor temperature';
        }
        return 'Gas vs outdoor temperature';
    }

    private static isConnectionAvailable(connection: {info?: {desiredStartDate?: string; desiredEndDate?: string}}): boolean {
        const now = Date.now();
        const start = connection.info?.desiredStartDate ? Date.parse(connection.info.desiredStartDate) : null;
        const end = connection.info?.desiredEndDate ? Date.parse(connection.info.desiredEndDate) : null;

        return (!Number.isFinite(start) || start <= now) && (!Number.isFinite(end) || end > now);
    }
}

export interface ChartModalOptions {
    fullScreen: boolean;
}
